import React, { useEffect, useState } from "react";
import { getBackendBaseUrl } from "../../utils/apiUrl";
import {
  FaPlus, FaEdit, FaTrash, FaRobot, FaUsers, FaTable,
  FaChartBar, FaExchangeAlt, FaCog, FaCheckCircle, FaSpinner,
  FaAngleRight, FaFileAlt, FaInfoCircle, FaLink, FaDatabase,
  FaHistory, FaTimes, FaComment, FaUser, FaPhone, FaEnvelope,
  FaFire, FaSnowflake, FaStickyNote, FaRobot as FaBot
} from "react-icons/fa";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line, Legend
} from "recharts";
import { toast } from "react-toastify";

const CAMPAIGN_TYPES = [
  "Website Chatbot",
  "Landing Page",
  "Facebook",
  "Instagram",
  "WhatsApp",
  "Google Ads",
  "Meta Ads",
  "Email Campaign",
  "Trade Show",
  "Referral",
  "Manual",
  "Excel Import",
  "Other"
];

const CAMPAIGN_STATUSES = ["Draft", "Active", "Paused", "Completed", "Archived"];

const PIPELINE_STAGES = [
  "New",
  "Contacted",
  "Qualified",
  "Appointment Scheduled",
  "Test Drive",
  "Negotiation",
  "Finance",
  "Won",
  "Lost"
];

export default function CampaignsDashboard() {
  const [activeTab, setActiveTab] = useState("campaigns"); // campaigns, templates, leads, pipeline, analytics
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [salesTeam, setSalesTeam] = useState([]);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection states
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLeads, setCampaignLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [orderedFields, setOrderedFields] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);

  // Lead Timeline Drawer
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineLead, setTimelineLead] = useState(null);
  const [timelineActivities, setTimelineActivities] = useState([]);
  const [timelineConversation, setTimelineConversation] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [newNote, setNewNote] = useState("");

  // Modals / Forms states
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Campaign Form State
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    description: "",
    campaignType: "Website Chatbot",
    status: "Active",
    leadTemplateId: "",
    assignedSalesTeam: [],
    assignedChatbots: [],
    source: "",
    tags: "",
    assignmentRule: "Round Robin",
    crmSyncConfig: {
      enabled: false,
      provider: "Webhook",
      endpointUrl: "",
      apiKey: ""
    }
  });

  // Template Form State
  const [templateForm, setTemplateForm] = useState({
    name: "",
    fields: [
      { fieldId: "name", fieldName: "Name", fieldType: "text", required: true },
      { fieldId: "phone", fieldName: "Phone", fieldType: "phone", required: true },
      { fieldId: "email", fieldName: "Email", fieldType: "email", required: true },
      { fieldId: "interestedVehicle", fieldName: "Interested Vehicle", fieldType: "text", required: false }
    ]
  });

  const getHeaders = () => {
    const token = localStorage.getItem("authToken");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  const getApiUrl = () => {
    return getBackendBaseUrl();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();

      // Campaigns
      const campRes = await fetch(`${apiBase}/campaigns`, { headers });
      const campData = await campRes.json();
      if (campData.success) setCampaigns(campData.data);

      // Templates
      const tempRes = await fetch(`${apiBase}/lead-templates`, { headers });
      const tempData = await tempRes.json();
      if (tempData.success) setTemplates(tempData.data);

      // Sales Team
      const teamRes = await fetch(`${apiBase}/users`, { headers });
      const teamData = await teamRes.json();
      if (teamData.success) {
        setSalesTeam(teamData.data.filter(u => u.role !== "Owner"));
      }

      // Chatbots
      const botRes = await fetch(`${apiBase}/chatbot/agents`, { headers });
      const botData = await botRes.json();
      if (botData.success) setChatbots(botData.data);

    } catch (err) {
      console.error("Fetch initial dashboard data failed", err);
      toast.error("Failed to load campaign data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadCampaignLeads = async (campaignId) => {
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/campaign/${campaignId}`, { headers });
      const data = await res.json();
      if (data.success) {
        setCampaignLeads(data.data);
      }
    } catch (err) {
      console.error("Fetch leads failed", err);
    }
  };

  const loadCampaignAnalytics = async (campaignId) => {
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/analytics-manager/campaign/${campaignId}/dashboard`, { headers });
      const data = await res.json();
      if (data.success) {
        setAnalytics(data.data);
      }
    } catch (err) {
      console.error("Fetch analytics failed", err);
    }
  };

  useEffect(() => {
    if (selectedCampaign) {
      loadCampaignLeads(selectedCampaign._id);
      loadCampaignAnalytics(selectedCampaign._id);
      const fields = selectedCampaign?.leadTemplateId?.fields || [];
      setOrderedFields(fields);
      setSelectedLeadIds([]);
    } else if (campaigns.length > 0) {
      setSelectedCampaign(campaigns[0]);
    }
  }, [selectedCampaign, campaigns]);

  // Campaign Handlers
  const handleCampaignSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const payload = {
        ...campaignForm,
        tags: campaignForm.tags.split(",").map(t => t.trim()).filter(t => t)
      };

      let res;
      if (editingCampaign) {
        res = await fetch(`${apiBase}/campaigns/${editingCampaign._id}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${apiBase}/campaigns`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload)
        });
      }

      const data = await res.json();
      if (data.success) {
        toast.success(editingCampaign ? "Campaign updated" : "Campaign created");
        setShowCampaignModal(false);
        setEditingCampaign(null);
        fetchData();
      } else {
        toast.error(data.message || "Failed to save campaign");
      }
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm("Are you sure you want to archive/delete this campaign?")) return;
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/campaigns/${id}`, {
        method: "DELETE",
        headers
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Campaign archived successfully");
        fetchData();
      }
    } catch (err) {
      toast.error("Archive failed");
    }
  };

  // Lead Template Handlers
  const handleAddField = () => {
    setTemplateForm(prev => ({
      ...prev,
      fields: [...prev.fields, { fieldId: `field_${Date.now()}`, fieldName: "New Field", fieldType: "text", required: false }]
    }));
  };

  const handleRemoveField = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleFieldChange = (index, key, val) => {
    const updated = [...templateForm.fields];
    updated[index][key] = val;
    if (key === "fieldName") {
      updated[index]["fieldId"] = val.toLowerCase().replace(/[^a-z0-9]/g, "_");
    }
    setTemplateForm(prev => ({ ...prev, fields: updated }));
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/lead-templates`, {
        method: "POST",
        headers,
        body: JSON.stringify(templateForm)
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lead template created successfully");
        setShowTemplateModal(false);
        setTemplateForm({
          name: "",
          fields: [
            { fieldId: "name", fieldName: "Name", fieldType: "text", required: true },
            { fieldId: "phone", fieldName: "Phone", fieldType: "phone", required: true },
            { fieldId: "email", fieldName: "Email", fieldType: "email", required: true }
          ]
        });
        fetchData();
      }
    } catch (err) {
      toast.error("Template creation failed");
    }
  };

  // Lead Pipeline Handlers
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${leadId}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lead stage updated");
        if (selectedCampaign) loadCampaignLeads(selectedCampaign._id);
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const updateLeadTemperature = async (leadId, newTemp) => {
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${leadId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ temperature: newTemp })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Temperature updated");
        if (selectedCampaign) loadCampaignLeads(selectedCampaign._id);
      }
    } catch (err) {
      toast.error("Failed to update temperature");
    }
  };

  const handleAssignSalesperson = async (leadId, salespersonId) => {
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${leadId}/assign`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ assignedSalesperson: salespersonId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Salesperson assigned successfully");
        if (selectedCampaign) loadCampaignLeads(selectedCampaign._id);
      }
    } catch (err) {
      toast.error("Failed to assign salesperson");
    }
  };

  // Column Drag and Drop Handlers
  const handleColumnDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
  };

  const handleColumnDrop = (e, targetIdx) => {
    const sourceIdx = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(sourceIdx) || sourceIdx === targetIdx) return;
    const updated = [...orderedFields];
    const temp = updated[sourceIdx];
    updated[sourceIdx] = updated[targetIdx];
    updated[targetIdx] = temp;
    setOrderedFields(updated);
  };

  // Inline Cell Double Click Handlers
  const handleCellDoubleClick = (lead, fieldId, currentVal) => {
    setEditingCell({ leadId: lead._id, fieldId });
    setEditValue(currentVal || "");
  };

  const handleSaveCell = async (lead, fieldId) => {
    const valueObj = lead.leadData?.find(d => d.fieldId === fieldId);
    let originalVal = valueObj ? valueObj.fieldValue : "";
    if (!originalVal) {
      if (fieldId === "name") originalVal = lead.name;
      if (fieldId === "phone") originalVal = lead.phone;
      if (fieldId === "email") originalVal = lead.email;
    }

    if (String(originalVal) === String(editValue)) {
      setEditingCell(null);
      return;
    }

    const updatedLeadData = [...(lead.leadData || [])];
    const idx = updatedLeadData.findIndex(d => d.fieldId === fieldId);
    if (idx !== -1) {
      updatedLeadData[idx].fieldValue = editValue;
    } else {
      const fieldObj = selectedCampaign?.leadTemplateId?.fields.find(f => f.fieldId === fieldId);
      updatedLeadData.push({
        fieldId,
        fieldName: fieldObj ? fieldObj.fieldName : fieldId,
        fieldValue: editValue
      });
    }

    const updatePayload = { leadData: updatedLeadData };
    if (fieldId === "name") updatePayload.name = editValue;
    if (fieldId === "email") updatePayload.email = editValue;
    if (fieldId === "phone") updatePayload.phone = editValue;

    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${lead._id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(updatePayload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lead field updated!");
        setEditingCell(null);
        if (selectedCampaign) loadCampaignLeads(selectedCampaign._id);
      } else {
        toast.error(data.message || "Failed to update lead");
      }
    } catch (err) {
      toast.error("Network error during update");
    }
  };

  const handleBulkAssign = async (salespersonId) => {
    if (!salespersonId) return;
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      setLoading(true);
      await Promise.all(selectedLeadIds.map(leadId =>
        fetch(`${apiBase}/leads-manager/${leadId}/assign`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({ assignedSalesperson: salespersonId })
        })
      ));
      toast.success(`Assigned ${selectedLeadIds.length} leads successfully`);
      setSelectedLeadIds([]);
      if (selectedCampaign) loadCampaignLeads(selectedCampaign._id);
    } catch (err) {
      toast.error("Failed bulk assignment");
    } finally {
      setLoading(false);
    }
  };

  const loadLeadTimeline = async (lead) => {
    setTimelineLead(lead);
    setTimelineOpen(true);
    setTimelineLoading(true);
    setTimelineActivities([]);
    setTimelineConversation(null);
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${lead._id}/timeline`, { headers });
      const data = await res.json();
      if (data.success) {
        setTimelineActivities(data.data.activities || []);
        setTimelineConversation(data.data.conversation || null);
        setTimelineLead(data.data.lead || lead);
      }
    } catch (err) {
      toast.error("Failed to load lead timeline");
    } finally {
      setTimelineLoading(false);
    }
  };

  const handlePostNote = async () => {
    if (!newNote.trim() || !timelineLead) return;
    try {
      const apiBase = getApiUrl();
      const headers = getHeaders();
      const res = await fetch(`${apiBase}/leads-manager/${timelineLead._id}/notes`, {
        method: "POST",
        headers,
        body: JSON.stringify({ note: newNote.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setTimelineActivities(prev => [...prev, data.data]);
        setNewNote("");
        toast.success("Note added!");
      } else {
        toast.error("Failed to add note");
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center text-slate-800">
        <FaSpinner className="animate-spin text-3xl text-indigo-600 mr-2" />
        <span className="font-semibold text-lg">Loading Campaign System...</span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen text-slate-800">
      {/* Upper Navigation & Campaign Selector */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Campaign Operations</h1>
          <p className="text-slate-500 text-sm mt-1">Manage marketing campaigns, lead extraction rules, and CRM sync.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">Active Campaign:</span>
            <select
              className="px-4 py-2 border border-slate-300 rounded-xl bg-white text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium w-full md:w-48"
              value={selectedCampaign?._id || ""}
              onChange={(e) => {
                const selected = campaigns.find(c => c._id === e.target.value);
                setSelectedCampaign(selected);
              }}
            >
              {campaigns.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => {
              setCampaignForm({
                name: "",
                description: "",
                campaignType: "Website Chatbot",
                status: "Active",
                leadTemplateId: "",
                assignedSalesTeam: [],
                assignedChatbots: [],
                source: "",
                tags: "",
                assignmentRule: "Round Robin",
                crmSyncConfig: {
                  enabled: false,
                  provider: "Webhook",
                  endpointUrl: "",
                  apiKey: ""
                }
              });
              setEditingCampaign(null);
              setShowCampaignModal(true);
            }}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-3 rounded-xl shadow-md transition text-xs whitespace-nowrap"
          >
            <FaPlus size={10} /> Create Campaign
          </button>

          <button
            onClick={() => {
              setTemplateForm({
                name: "",
                fields: [
                  { fieldId: "name", fieldName: "Name", fieldType: "text", required: true },
                  { fieldId: "phone", fieldName: "Phone", fieldType: "phone", required: true },
                  { fieldId: "email", fieldName: "Email", fieldType: "email", required: true },
                  { fieldId: "interestedVehicle", fieldName: "Interested Vehicle", fieldType: "text", required: false }
                ]
              });
              setShowTemplateModal(true);
            }}
            className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-3 rounded-xl shadow-sm transition text-xs whitespace-nowrap"
          >
            <FaPlus size={10} /> Create Template
          </button>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="flex border-b border-slate-200 gap-2 mb-6 overflow-x-auto pb-1 scrollbar-hide">
        {[
          { id: "campaigns", label: "Campaigns List", icon: FaRobot },
          { id: "templates", label: "Lead Templates", icon: FaFileAlt },
          { id: "leads", label: "Leads Table", icon: FaTable },
          { id: "analytics", label: "Campaign Analytics", icon: FaChartBar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-xl transition-all whitespace-nowrap ${activeTab === tab.id
              ? "bg-white border-t border-x border-slate-200 text-indigo-600 shadow-sm"
              : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content Panels */}
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 min-h-[400px]">

        {/* TAB 1: CAMPAIGNS LIST */}
        {activeTab === "campaigns" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">Dealership Marketing Campaigns</h3>
              <button
                onClick={() => {
                  setCampaignForm({
                    name: "",
                    description: "",
                    campaignType: "Website Chatbot",
                    status: "Active",
                    leadTemplateId: templates[0]?._id || "",
                    assignedSalesTeam: [],
                    assignedChatbots: [],
                    source: "",
                    tags: "",
                    assignmentRule: "Round Robin",
                    crmSyncConfig: { enabled: false, provider: "Webhook", endpointUrl: "", apiKey: "" }
                  });
                  setEditingCampaign(null);
                  setShowCampaignModal(true);
                }}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-md transition text-sm"
              >
                <FaPlus size={12} /> Add Campaign
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-4 px-4">Campaign Name</th>
                    <th className="py-4 px-4">Type</th>
                    <th className="py-4 px-4">Status</th>
                    <th className="py-4 px-4">Lead Template</th>
                    <th className="py-4 px-4">Sales Team</th>
                    <th className="py-4 px-4">CRM Sync</th>
                    <th className="py-4 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(camp => (
                    <tr key={camp._id} className="border-b border-slate-100 hover:bg-slate-50/40 transition">
                      <td className="py-4 px-4">
                        <div className="font-bold text-slate-800">{camp.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{camp.description}</div>
                      </td>
                      <td className="py-4 px-4 text-sm font-medium">{camp.campaignType}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${camp.status === "Active" ? "bg-emerald-50 text-emerald-700" :
                          camp.status === "Paused" ? "bg-amber-50 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                          {camp.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm">{camp.leadTemplateId?.name || "Not Linked"}</td>
                      <td className="py-4 px-4 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <FaUsers /> {camp.assignedSalesTeam?.length || 0} Members
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {camp.crmSyncConfig?.enabled ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm font-semibold">
                            <FaCheckCircle /> Active
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">Disabled</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCampaign(camp);
                              setCampaignForm({
                                ...camp,
                                leadTemplateId: camp.leadTemplateId?._id || "",
                                assignedSalesTeam: camp.assignedSalesTeam?.map(t => t._id) || [],
                                tags: camp.tags?.join(", ") || ""
                              });
                              setShowCampaignModal(true);
                            }}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteCampaign(camp._id)}
                            className="p-2 text-slate-500 hover:text-rose-600 hover:bg-slate-100 rounded-lg transition"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 2: LEAD TEMPLATES */}
        {activeTab === "templates" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">Dynamic Lead Capture Forms</h3>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-md transition text-sm"
              >
                <FaPlus size={12} /> Create Template
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {templates.map(temp => (
                <div key={temp._id} className="border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
                    <h4 className="font-bold text-lg text-slate-800">{temp.name}</h4>
                    <span className="text-xs bg-slate-100 text-slate-500 font-semibold py-1 px-2.5 rounded-full">
                      {temp.fields.length} Fields
                    </span>
                  </div>
                  <div className="space-y-2">
                    {temp.fields.map(f => (
                      <div key={f.fieldId} className="flex justify-between items-center bg-slate-50 px-3 py-2 rounded-xl text-sm">
                        <span className="font-medium text-slate-700">{f.fieldName}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400 capitalize">{f.fieldType}</span>
                          {f.required && (
                            <span className="text-[10px] bg-rose-50 text-rose-700 font-bold px-1.5 py-0.5 rounded border border-rose-200">
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 3: DYNAMIC LEADS TABLE */}
        {activeTab === "leads" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-800">Captured Campaign Leads</h3>
                <p className="text-sm text-slate-500 mt-1">Showing leads captured in: <span className="font-semibold text-indigo-600">{selectedCampaign?.name}</span></p>
              </div>
            </div>

            {selectedLeadIds.length > 0 && (
              <div className="flex flex-col md:flex-row justify-between items-center bg-indigo-50 border border-indigo-100 rounded-2xl p-4 mb-6 gap-3">
                <span className="text-sm text-indigo-800 font-bold">
                  {selectedLeadIds.length} lead(s) selected
                </span>
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <select
                    className="px-3 py-2 border border-slate-300 rounded-xl bg-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full md:w-56"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkAssign(e.target.value);
                        e.target.value = "";
                      }
                    }}
                  >
                    <option value="">Bulk Assign Selected To...</option>
                    {selectedCampaign?.assignedSalesTeam?.map(user => (
                      <option key={user._id} value={user._id}>{user.email}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-4 px-4 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={campaignLeads.length > 0 && selectedLeadIds.length === campaignLeads.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeadIds(campaignLeads.map(l => l._id));
                          } else {
                            setSelectedLeadIds([]);
                          }
                        }}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                      />
                    </th>
                    {/* Dynamic headers derived from campaign's lead template */}
                    {orderedFields.map((field, idx) => (
                      <th
                        key={field.fieldId}
                        className="py-4 px-4 cursor-move hover:bg-slate-100 transition select-none"
                        draggable="true"
                        onDragStart={(e) => handleColumnDragStart(e, idx)}
                        onDragOver={(e) => handleColumnDragOver(e)}
                        onDrop={(e) => handleColumnDrop(e, idx)}
                      >
                        <div className="flex items-center gap-1.5">
                          <span>{field.fieldName}</span>
                          <span className="text-[10px] text-slate-300 font-normal">⋮⋮</span>
                        </div>
                      </th>
                    ))}
                    <th className="py-4 px-4">Temperature</th>
                    <th className="py-4 px-4">Pipeline Status</th>
                    <th className="py-4 px-4 text-center">Re-assign</th>
                    <th className="py-4 px-4 text-center">History</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignLeads.map(lead => {
                    const isSelected = selectedLeadIds.includes(lead._id);
                    return (
                      <tr key={lead._id} className={`border-b border-slate-100 transition ${isSelected ? "bg-indigo-50/20" : "hover:bg-slate-50/40"}`}>
                        <td className="py-4 px-4 text-center">
                          <input 
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLeadIds(prev => [...prev, lead._id]);
                              } else {
                                setSelectedLeadIds(prev => prev.filter(id => id !== lead._id));
                              }
                            }}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
                          />
                        </td>
                        {orderedFields.map(field => {
                          const valueObj = lead.leadData?.find(d => d.fieldId === field.fieldId);
                          let displayValue = valueObj ? valueObj.fieldValue : "";

                          // Compatibility fallbacks for basic fields
                          if (!displayValue) {
                            if (field.fieldId === "name") displayValue = lead.name;
                            if (field.fieldId === "phone") displayValue = lead.phone;
                            if (field.fieldId === "email") displayValue = lead.email;
                          }

                          const isEditing = editingCell?.leadId === lead._id && editingCell?.fieldId === field.fieldId;

                          return (
                            <td
                              key={field.fieldId}
                              className="py-4 px-4 text-sm font-medium text-slate-700 min-w-[120px]"
                              onDoubleClick={() => handleCellDoubleClick(lead, field.fieldId, displayValue)}
                            >
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleSaveCell(lead, field.fieldId)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSaveCell(lead, field.fieldId);
                                    if (e.key === "Escape") setEditingCell(null);
                                  }}
                                  autoFocus
                                  className="w-full bg-white border border-indigo-400 text-slate-800 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                              ) : (
                                <span className="hover:border-b hover:border-dashed hover:border-slate-400 cursor-pointer block py-0.5" title="Double click to edit">
                                  {displayValue === true ? "Yes" : displayValue === false ? "No" : String(displayValue || "-")}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-4 px-4">
                          <select
                            className={`text-xs font-bold py-1.5 px-2.5 rounded-xl border focus:outline-none ${
                              lead.temperature === "Hot" ? "bg-red-50 border-red-200 text-red-700" :
                              lead.temperature === "Warm" ? "bg-amber-50 border-amber-200 text-amber-700" :
                              "bg-blue-50 border-blue-200 text-blue-700"
                            }`}
                            value={lead.temperature || "Cold"}
                            onChange={(e) => updateLeadTemperature(lead._id, e.target.value)}
                          >
                            <option value="Hot">🔥 Hot</option>
                            <option value="Warm">☀️ Warm</option>
                            <option value="Cold">❄️ Cold</option>
                          </select>
                        </td>
                        <td className="py-4 px-4">
                          <select
                            className="bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold py-1.5 px-3 rounded-xl focus:outline-none"
                            value={lead.status}
                            onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                          >
                            {selectedCampaign?.pipelineStages?.map(stage => (
                              <option key={stage} value={stage}>{stage}</option>
                            ))}
                          </select>
                        </td>

                        <td className="py-4 px-4">
                          <select
                            className="bg-slate-100 border border-slate-200 text-slate-700 text-xs py-1.5 px-2 rounded-xl focus:outline-none w-full"
                            value={lead.assignedSalesperson?._id || ""}
                            onChange={(e) => handleAssignSalesperson(lead._id, e.target.value)}
                          >
                            <option value="">Choose User...</option>
                            {selectedCampaign?.assignedSalesTeam?.map(user => (
                              <option key={user._id} value={user._id}>{user.email}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => loadLeadTimeline(lead)}
                            className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-2.5 py-1.5 rounded-lg transition"
                            title="View Lead History"
                          >
                            <FaHistory size={12} /> History
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {campaignLeads.length === 0 && (
                    <tr>
                      <td colSpan={(orderedFields.length || 0) + 6} className="py-12 text-center text-slate-400 font-medium">
                        No leads captured yet for this campaign.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LEAD TIMELINE DRAWER */}
        {timelineOpen && (
          <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setTimelineOpen(false)}>
            <div
              className="w-full max-w-lg bg-white h-full shadow-2xl overflow-y-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-600 to-violet-600">
                <div>
                  <h2 className="text-white font-extrabold text-lg flex items-center gap-2">
                    <FaHistory size={16} /> Lead Profile &amp; Timeline
                  </h2>
                  <p className="text-indigo-200 text-xs mt-0.5">{timelineLead?.name || "Unknown"}</p>
                </div>
                <button onClick={() => setTimelineOpen(false)} className="text-white hover:text-indigo-200 transition p-1">
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Lead Profile Card */}
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-extrabold text-xl flex-shrink-0">
                    {(timelineLead?.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-slate-900 font-bold text-base">{timelineLead?.name || "Unknown Visitor"}</h3>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      {timelineLead?.email && <span className="flex items-center gap-1 text-xs text-slate-500"><FaEnvelope size={10} /> {timelineLead.email}</span>}
                      {timelineLead?.phone && <span className="flex items-center gap-1 text-xs text-slate-500"><FaPhone size={10} /> {timelineLead.phone}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                        timelineLead?.temperature === "Warm" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                      }`}>
                        {timelineLead?.temperature === "Warm" ? <FaFire size={10} /> : <FaSnowflake size={10} />}
                        {timelineLead?.temperature || "Cold"}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700">
                        {timelineLead?.status || "New"}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {timelineLead?.source || "Chatbot"}
                      </span>
                    </div>
                    {timelineLead?.intent && (
                      <p className="text-xs text-slate-500 italic mt-2 bg-white border border-slate-100 rounded-lg px-3 py-2">
                        💡 {timelineLead.intent}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Note Input */}
              <div className="px-6 py-3 border-b border-slate-100">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Post comment/note to timeline..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handlePostNote()}
                    className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 text-slate-800"
                  />
                  <button
                    onClick={handlePostNote}
                    disabled={!newNote.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-xl text-sm transition"
                  >
                    Post
                  </button>
                </div>
              </div>

              {/* Timeline */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Activity History</p>

                {timelineLoading ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-indigo-600">
                    <FaSpinner className="animate-spin" /> Loading timeline...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timelineActivities.map((act, i) => {
                      const iconMap = {
                        lead_created: { icon: "🎯", color: "bg-emerald-100 text-emerald-700" },
                        status_change: { icon: "🔄", color: "bg-blue-100 text-blue-700" },
                        assignment: { icon: "👤", color: "bg-violet-100 text-violet-700" },
                        note: { icon: "📝", color: "bg-amber-100 text-amber-700" },
                        field_edit: { icon: "✏️", color: "bg-slate-100 text-slate-700" },
                        chatbot_message: { icon: "🤖", color: "bg-indigo-100 text-indigo-700" },
                      };
                      const meta = iconMap[act.type] || { icon: "•", color: "bg-slate-100 text-slate-600" };
                      return (
                        <div key={act._id || i} className="flex gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <span className={`w-8 h-8 rounded-full ${meta.color} flex items-center justify-center text-sm`}>{meta.icon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-bold text-slate-700">{act.performedByName || "System"}</span>
                              <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                {new Date(act.createdAt).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-slate-800 mt-0.5">{act.title}</p>
                            {act.description && (
                              <p className="text-xs text-slate-500 mt-1 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                {act.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Chatbot Conversation Transcript */}
                    {timelineConversation?.messages?.length > 0 && (
                      <div className="mt-6">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">💬 Chatbot Conversation Transcript</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto bg-slate-50 rounded-2xl p-3 border border-slate-100">
                          {timelineConversation.messages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs ${
                                msg.role === "user"
                                  ? "bg-indigo-600 text-white rounded-br-none"
                                  : "bg-white border border-slate-200 text-slate-700 rounded-bl-none"
                              }`}>
                                <span className="block font-bold mb-0.5 opacity-70">
                                  {msg.role === "user" ? "Visitor" : "Assistant"}
                                </span>
                                {msg.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {timelineActivities.length === 0 && !timelineConversation && (
                      <div className="text-center py-12 text-slate-400 text-sm">
                        No activity recorded yet for this lead.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: KANBAN CRM PIPELINE */}
        {activeTab === "pipeline" && (
          <div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Interactive CRM Kanban Board</h3>
            <p className="text-sm text-slate-500 mb-6">Drag and drop/update your leads across pipeline stages.</p>

            <div className="flex gap-4 overflow-x-auto pb-4 items-start select-none">
              {PIPELINE_STAGES.map(stage => {
                const stageLeads = campaignLeads.filter(l => l.status === stage);
                return (
                  <div key={stage} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 w-72 shrink-0">
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-extrabold text-sm text-slate-700 uppercase tracking-wider">{stage}</span>
                      <span className="bg-indigo-50 text-indigo-700 text-xs font-bold py-0.5 px-2.5 rounded-full">
                        {stageLeads.length}
                      </span>
                    </div>

                    <div className="space-y-3 min-h-[300px]">
                      {stageLeads.map(lead => (
                        <div key={lead._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition cursor-pointer">
                          <h5 className="font-bold text-slate-800 text-sm">{lead.name || "Anonymous Lead"}</h5>
                          <div className="text-xs text-slate-400 mt-1">{lead.email}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{lead.phone}</div>

                          <div className="border-t border-slate-100 my-3 pt-2 flex justify-between items-center">
                            <span className="text-[10px] text-slate-400 font-medium">
                              Rep: {lead.assignedSalesperson?.email?.split("@")[0] || "Unassigned"}
                            </span>

                            <select
                              className="text-[10px] font-bold bg-slate-100 hover:bg-indigo-50 text-indigo-700 rounded-lg p-1 border-none focus:outline-none"
                              value={lead.status}
                              onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                            >
                              {PIPELINE_STAGES.map(stg => (
                                <option key={stg} value={stg}>{stg}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB 5: CAMPAIGN ANALYTICS */}
        {activeTab === "analytics" && (
          <div>
            <h3 className="text-xl font-bold text-slate-800 mb-6">Real-Time Campaign Analytics</h3>
            {analytics ? (
              <div className="space-y-8">
                {/* Stats Cards grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4 text-center">
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Visitors</div>
                    <div className="text-3xl font-extrabold text-slate-900 mt-1">{analytics.visitors}</div>
                  </div>
                  <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4 text-center">
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Conversations</div>
                    <div className="text-3xl font-extrabold text-slate-900 mt-1">{analytics.conversations}</div>
                  </div>
                  <div className="border border-emerald-100 bg-emerald-50/50 rounded-2xl p-4 text-center">
                    <div className="text-emerald-500 text-xs font-bold uppercase tracking-wider">Generated Leads</div>
                    <div className="text-3xl font-extrabold text-emerald-700 mt-1">{campaignLeads.length}</div>
                  </div>
                  <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4 text-center">
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider">Qualified Leads</div>
                    <div className="text-3xl font-extrabold text-slate-900 mt-1">{analytics.qualifiedLeads}</div>
                  </div>
                  <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4 text-center">
                    <div className="text-slate-400 text-xs font-bold uppercase tracking-wider font-sans">Conversion Rate</div>
                    <div className="text-3xl font-extrabold text-indigo-600 mt-1">{analytics.conversionRate}%</div>
                  </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="border border-slate-100 rounded-2xl p-5 bg-slate-50/30">
                    <h4 className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wide">Leads by Pipeline Stage</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.leadsByStatus || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="status" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="border border-slate-100 rounded-2xl p-5 bg-slate-50/30">
                    <h4 className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wide">Top Interested Vehicles</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.topVehicles || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="vehicle" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-12 text-center text-slate-400 font-medium">
                Select a campaign to load real-time insights.
              </div>
            )}
          </div>
        )}
      </div>

      {/* CAMPAIGN CREATE/EDIT MODAL */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black text-slate-900 mb-4">
              {editingCampaign ? "Edit Marketing Campaign" : "Add Marketing Campaign"}
            </h3>

            <form onSubmit={handleCampaignSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Campaign Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Campaign Type</label>
                  <select
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                    value={campaignForm.campaignType}
                    onChange={(e) => setCampaignForm({ ...campaignForm, campaignType: e.target.value })}
                  >
                    {CAMPAIGN_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description</label>
                <textarea
                  className="w-full px-4 py-2 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Lead Template</label>
                  <select
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                    value={campaignForm.leadTemplateId}
                    onChange={(e) => setCampaignForm({ ...campaignForm, leadTemplateId: e.target.value })}
                    required
                  >
                    <option value="">Select Template...</option>
                    {templates.map(t => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Status</label>
                  <select
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                    value={campaignForm.status}
                    onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value })}
                  >
                    {CAMPAIGN_STATUSES.map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assignment settings */}
              <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4">
                <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-1.5">
                  <FaUsers /> Sales Team Assignment & Rules
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Assignment Rule</label>
                    <select
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                      value={campaignForm.assignmentRule}
                      onChange={(e) => setCampaignForm({ ...campaignForm, assignmentRule: e.target.value })}
                    >
                      <option value="Round Robin">Round Robin</option>
                      <option value="Manual">Manual Assignment</option>
                      <option value="Fixed User">Fixed User</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sales Team Members</label>
                    <div className="max-h-24 overflow-y-auto border border-slate-200 rounded-xl bg-white p-2">
                      {salesTeam.map(user => (
                        <label key={user._id} className="flex items-center gap-2 py-0.5 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={campaignForm.assignedSalesTeam.includes(user._id)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCampaignForm(prev => ({
                                ...prev,
                                assignedSalesTeam: checked
                                  ? [...prev.assignedSalesTeam, user._id]
                                  : prev.assignedSalesTeam.filter(id => id !== user._id)
                              }));
                            }}
                          />
                          {user.email}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chatbot mapping */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Assign Chatbot Agents</label>
                <div className="max-h-24 overflow-y-auto border border-slate-200 rounded-xl bg-white p-2.5">
                  {chatbots.map(bot => (
                    <label key={bot.id} className="flex items-center gap-2 py-0.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={campaignForm.assignedChatbots.includes(bot.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setCampaignForm(prev => ({
                            ...prev,
                            assignedChatbots: checked
                              ? [...prev.assignedChatbots, bot.id]
                              : prev.assignedChatbots.filter(id => id !== bot.id)
                          }));
                        }}
                      />
                      🤖 {bot.name || bot.id}
                    </label>
                  ))}
                </div>
              </div>

              {/* CRM webhook config */}
              <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-sm text-slate-800 flex items-center gap-1.5">
                    <FaLink /> CRM webhook Integration
                  </h4>
                  <label className="flex items-center gap-1 cursor-pointer font-semibold text-xs text-indigo-600">
                    <input
                      type="checkbox"
                      checked={campaignForm.crmSyncConfig.enabled}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        crmSyncConfig: { ...campaignForm.crmSyncConfig, enabled: e.target.checked }
                      })}
                    />
                    Enable Integration
                  </label>
                </div>

                {campaignForm.crmSyncConfig.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Endpoint Webhook URL</label>
                      <input
                        type="url"
                        placeholder="https://your-crm.com/leads"
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold"
                        value={campaignForm.crmSyncConfig.endpointUrl}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          crmSyncConfig: { ...campaignForm.crmSyncConfig, endpointUrl: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Bearer API Key</label>
                      <input
                        type="text"
                        placeholder="API Key / Auth Token"
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-semibold"
                        value={campaignForm.crmSyncConfig.apiKey}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          crmSyncConfig: { ...campaignForm.crmSyncConfig, apiKey: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCampaignModal(false)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-5 rounded-xl transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl shadow-md transition text-sm"
                >
                  Save Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LEAD TEMPLATE CREATE MODAL */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black text-slate-900 mb-4">Create Lead Template</h3>

            <form onSubmit={handleTemplateSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Template Name</label>
                <input
                  type="text"
                  placeholder="e.g., Automotive Standard"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-semibold"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-xs font-bold text-slate-500 uppercase">Configured Fields</label>
                  <button
                    type="button"
                    onClick={handleAddField}
                    className="flex items-center gap-1 text-xs text-indigo-600 font-bold hover:text-indigo-800"
                  >
                    <FaPlus size={10} /> Add Field
                  </button>
                </div>

                <div className="space-y-3.5 max-h-72 overflow-y-auto pr-1">
                  {templateForm.fields.map((field, idx) => (
                    <div key={idx} className="flex gap-2.5 items-center border border-slate-200 rounded-xl p-3 bg-slate-50">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Field Label"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-xs font-semibold"
                          value={field.fieldName}
                          onChange={(e) => handleFieldChange(idx, "fieldName", e.target.value)}
                        />
                      </div>

                      <div className="w-28">
                        <select
                          className="w-full px-2 py-2 border border-slate-300 rounded-lg bg-white text-xs font-semibold"
                          value={field.fieldType}
                          onChange={(e) => handleFieldChange(idx, "fieldType", e.target.value)}
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="phone">Phone</option>
                          <option value="email">Email</option>
                          <option value="boolean">Boolean</option>
                        </select>
                      </div>

                      <label className="flex items-center gap-1 font-bold text-[10px] text-slate-500 select-none">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => handleFieldChange(idx, "required", e.target.checked)}
                        />
                        Req
                      </label>

                      {idx > 2 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveField(idx)}
                          className="p-1.5 text-slate-400 hover:text-rose-600"
                        >
                          <FaTrash size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-5 rounded-xl transition text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl shadow-md transition text-sm"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
